import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { Auth0ProviderWithHistory } from '@auth';
import { useAuthStore } from '@auth/stores';
import { NotificationContainer, useLayoutStore } from '@dynamon-as/ui';
import { ErrorPanelContainer } from '@components/ErrorPanel';
import { ThemeProvider } from '@mui/material/styles';
import { muiTheme } from '@dynamon-as/ui/themes';
import { routes } from './routes/Routes';
import { useAppStore } from '@stores';
import { setupErrorInterceptor } from '@api/dynoape';
import './index.css';

// Fonts - only import latin subset and weights we use (400, 500, 600, 700)
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/maven-pro/latin-400.css';
import '@fontsource/maven-pro/latin-500.css';
import '@fontsource/maven-pro/latin-600.css';
import '@fontsource/maven-pro/latin-700.css';

const container = document.getElementById('root');
if (!container) {
    throw new Error('Root element not found');
}
const root = createRoot(container);

// Initialize global app store
useAppStore.getState().setIsInitialized(true);

// dynoweb default: start with the @dynamon-as/ui navbar collapsed
// (the library layout store initializes as expanded)
useLayoutStore.setState({ isNavbarExpanded: false });

setupErrorInterceptor();

// Clear chunk reload flag on successful boot
sessionStorage.removeItem('chunk_reload_attempted');

const router = createBrowserRouter(routes, {});

// Read organization from query params
const searchParams = new URLSearchParams(window.location.search);
const orgFromQuery = searchParams.get('organization');

// If query param exists, store it as pending
if (orgFromQuery) {
    useAuthStore.getState().setPendingOrgId(orgFromQuery);
}

// Read organization from Zustand store to pass to Auth0Provider
const orgId = useAuthStore.getState().selectedOrgId;
const pendingOrgId = useAuthStore.getState().pendingOrgId;

// Use pending org if available, otherwise use selected org
const orgForAuth0 = pendingOrgId ?? orgId ?? undefined;

root.render(
    <Auth0ProviderWithHistory
        router={router}
        organization={orgForAuth0}
    >
        <ThemeProvider theme={muiTheme}>
            <NotificationContainer position="top-right" />
            <ErrorPanelContainer />
            <RouterProvider router={router} />
        </ThemeProvider>
    </Auth0ProviderWithHistory>,
);
